import React from 'react';
import {
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import LocationSearch from '../../LocationSearch';

const categories = [
  'Salon de coiffure',
  'Coiffeurs',
  'Barbiers',
  'Salons UV',
  'Spa',
  'Esthétique',
  'Manucure',
  'Épilations',
  'Massages',
  'Soins visage',
  'Soins corps',
  'Onglerie',
  'Maquillage',
  'Bronzage',
  'Extensions de cils'
];

const ProfileForm = ({
  formData,
  selectedLocation,
  loading,
  isMobile,
  onInputChange,
  onLocationChange,
  onSubmit,
  targetSection
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Scroll to target section when specified
  React.useEffect(() => {
    if (targetSection) {
      const element = document.getElementById(`section-${targetSection}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [targetSection]);

  return (
    <Box sx={{ width: '100%' }}>
      <form onSubmit={onSubmit}>
        <Stack spacing={3}>
          {/* Section Informations de base */}
          <Card 
            elevation={0} 
            sx={{ 
              border: '1px solid #e5e7eb',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                p: 2.5, 
                background: 'linear-gradient(135deg, #f5f5dc 0%, #faf8f3 100%)',
                borderBottom: '1px solid #e5e7eb'
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '1.1rem'
                }}
              >
                Informations de base
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Les informations essentielles de votre salon
              </Typography>
            </Box>
            
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nom du salon"
                    name="salonName"
                    value={formData.salonName}
                    onChange={onInputChange}
                    required
                    placeholder="Ex: Salon Belle Époque"
                    helperText="Le nom qui apparaîtra sur votre fiche publique"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6'
                          }
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nom du propriétaire"
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={onInputChange}
                    required
                    placeholder="Ex: Marie Dupont"
                    helperText="Votre nom ou celui du gérant"
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6'
                          }
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Téléphone"
                    name="phone"
                    value={formData.phone}
                    onChange={onInputChange}
                    required
                    placeholder="Ex: 01 23 45 67 89"
                    helperText="Numéro pour les réservations"
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6'
                          }
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Catégorie de salon</InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      label="Catégorie de salon"
                      onChange={onInputChange}
                      sx={{
                        bgcolor: 'white',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6'
                          }
                        }
                      }}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Section Localisation */}
          <Card 
            elevation={0} 
            sx={{ 
              border: '1px solid #e5e7eb',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                p: 2.5, 
                background: 'linear-gradient(135deg, #f0ead6 0%, #faf8f3 100%)',
                borderBottom: '1px solid #e5e7eb'
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '1.1rem'
                }}
              >
                Localisation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Où se trouve votre salon ?
              </Typography>
            </Box>
            
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
                      1. Recherchez votre ville
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Commencez par taper le nom de votre ville
                    </Typography>
                    <LocationSearch
                      value={selectedLocation ? selectedLocation.query : `${formData.city}, ${formData.region}`}
                      onChange={onLocationChange}
                      placeholder="Tapez votre ville (ex: Paris, Lyon, Marseille...)"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
                      2. Adresse complète
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Indiquez l'adresse exacte de votre salon
                    </Typography>
                    <TextField
                      fullWidth
                      label="Adresse complète"
                      name="address"
                      value={formData.address}
                      onChange={onInputChange}
                      multiline
                      rows={2}
                      placeholder="Ex: 123 Rue de la Paix, 75001 Paris"
                      helperText="Cette adresse sera visible par vos clients"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white',
                          '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#3b82f6'
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                </Grid>

                {(formData.city || formData.region) && (
                  <Grid item xs={12}>
                    <Box 
                      sx={{ 
                        p: 2, 
                        bgcolor: '#f0fdf4', 
                        borderRadius: 1, 
                        border: '1px solid #bbf7d0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#166534' }}>
                          Localisation détectée
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#15803d' }}>
                          {formData.city} {formData.region && `• ${formData.region}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Section Google My Business */}
          <Card
            elevation={0}
            sx={{
              border: '1px solid #e5e7eb',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                p: 2.5,
                background: 'linear-gradient(135deg, #dbeafe 0%, #faf8f3 100%)',
                borderBottom: '1px solid #e5e7eb'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '1.1rem'
                }}
              >
                <BusinessIcon sx={{ fontSize: 20 }} />
                Google My Business
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Affichez vos avis Google sur votre profil public
              </Typography>
            </Box>
            
            <CardContent sx={{ p: 3 }}>
              <TextField
                fullWidth
                label="Lien Google My Business (optionnel)"
                name="googleMyBusinessUrl"
                value={formData.googleMyBusinessUrl || ''}
                onChange={onInputChange}
                placeholder="https://maps.google.com/maps/place/..."
                helperText="Copiez le lien de votre fiche Google My Business pour afficher vos avis"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6'
                      }
                    }
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* Section Description */}
          <Card 
            elevation={0} 
            sx={{ 
              border: '1px solid #e5e7eb',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                p: 2.5, 
                background: 'linear-gradient(135deg, #e7e5e4 0%, #faf8f3 100%)',
                borderBottom: '1px solid #e5e7eb'
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '1.1rem'
                }}
              >
                Présentation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Décrivez votre salon et vos services
              </Typography>
            </Box>
            
            <CardContent sx={{ p: 3 }}>
              <TextField
                fullWidth
                label="Description de votre salon"
                name="description"
                value={formData.description}
                onChange={onInputChange}
                multiline
                rows={isTablet ? 4 : 5}
                placeholder="Présentez votre salon, vos spécialités, votre équipe, votre expérience, vos services..."
                helperText="Cette description apparaîtra sur votre fiche publique"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6'
                      }
                    }
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* Bouton de sauvegarde */}
          <Box sx={{ pt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              size="large"
              fullWidth={isTablet}
              sx={{
                py: 1.5,
                px: isTablet ? 0 : 6,
                fontSize: '1rem',
                fontWeight: 600,
                bgcolor: '#1c1917',
                '&:hover': {
                  bgcolor: '#292524'
                },
                ...(isTablet ? {} : {
                  display: 'block',
                  mx: 'auto',
                  minWidth: 300
                })
              }}
            >
              {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
};

export default ProfileForm;
